import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../context/WebSocketContext";
import { WidgetRecommendationsWebSocketMessage, WidgetRecommendationsRequest } from "../types";
import { Widget } from "@/modules";
import { config } from "@/config/env";

type WidgetRecommendationsState =
    | { status: "idle" }
    | { status: "loading"; step: string }
    | { status: "done"; recommendations: string; scenarioIds: string[]; benchmarkTrace?: Record<string, any> }
    | { status: "error"; error: string };

// Mirrors useWidgetInsightsWebSocket's shape, driven by WIDGET_RECOMMENDATION_EVENT_TRIGGER
// (see env.ts for the API Gateway registration caveat). Unlike insights, there's no `log`
// message type and no `visibleMetrics` request field - see widget-recommendations.types.ts.
export const useWidgetRecommendationsWebSocket = () => {
    const { state, sendMessage, isReady } = useWebSocketContext();
    const [recommendationsState, setRecommendationsState] = useState<WidgetRecommendationsState>({ status: "idle" });
    const sessionIdRef = useRef<string | null>(null);

    const wsAction = config.WIDGET_RECOMMENDATION_EVENT_TRIGGER;

    useEffect(() => {
        const msg = state.lastMessage as unknown as WidgetRecommendationsWebSocketMessage;
        if (!msg || msg.action !== wsAction) return;
        if (!sessionIdRef.current || msg.data?.sessionId !== sessionIdRef.current) return;

        const { data } = msg;

        if (data.type === "step") {
            setRecommendationsState({ status: "loading", step: data.step });
        } else if (data.type === "done") {
            setRecommendationsState({
                status: "done",
                recommendations: data.recommendations,
                scenarioIds: data.scenarioIds,
                benchmarkTrace: data.benchmarkTrace,
            });
        } else if (data.type === "error") {
            setRecommendationsState({ status: "error", error: data.error });
        }
    }, [state.lastMessage, wsAction]);

    const sendRequest = useCallback(
        (widget: Widget & { data: any }, dealerId: string, oemName?: string, productBudgets?: Record<string, string>) => {
            const sessionId = `widget-recommendations-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
            sessionIdRef.current = sessionId;
            setRecommendationsState({ status: "loading", step: "Starting..." });

            const baseRequest: WidgetRecommendationsRequest = {
                action: wsAction,
                sessionId,
                dealerId,
                widget,
                ...(oemName ? { oemName } : {}),
                ...(productBudgets ? { productBudgets } : {}),
            };

            const WS_SIZE_LIMIT = 100_000;
            const serialized = JSON.stringify(baseRequest);
            const request: WidgetRecommendationsRequest = serialized.length > WS_SIZE_LIMIT
                ? { action: wsAction, sessionId, dealerId, widget: { ...widget, data: undefined }, ...(oemName ? { oemName } : {}), ...(productBudgets ? { productBudgets } : {}) }
                : baseRequest;

            sendMessage(JSON.stringify(request));
        },
        [sendMessage, wsAction]
    );

    const reset = useCallback(() => {
        sessionIdRef.current = null;
        setRecommendationsState({ status: "idle" });
    }, []);

    return {
        isReady,
        recommendationsState,
        sendRequest,
        reset,
    };
};
